import api from '../utils/api';

/**
 * Timeout for POS order writes. When Wi-Fi is up but the internet is down,
 * requests would otherwise hang for the OS TCP timeout (30–120s) with the
 * cashier stuck on a spinner. After this timeout axios throws ECONNABORTED,
 * which offlineManager classifies as a network error and saves the order
 * locally for later sync. Reads and sync batches are NOT limited by this.
 */
// 7s: long enough for a warm Render dyno (~1-3s typical), short enough that a
// dead network fails over to local before staff perceive a freeze. Cold-start
// requests (>7s) fall back to local and reconcile via local_id dedup on sync,
// so a longer timeout only buys stall time, not safety.
const POS_WRITE_TIMEOUT_MS = 7000;

const orderService = {
  // Get all orders with optional filters
  getOrders: async (params = {}) => {
    try {
      const response = await api.get('orders', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get active orders for a branch.
  // 8s timeout (not the 30s axios default): these reads sit on hot UI paths
  // (table open, board refresh) — on a degraded network a 30s wait looks like
  // the app hanging. Fail fast; callers fall back to cached/Redux state.
  getActiveOrders: async (branchId) => {
    try {
      console.log('🔍 Fetching active orders for branch:', branchId);
      const response = await api.get(`orders/active?branchId=${branchId}`, { timeout: 8000 });
      console.log('📊 Backend returned orders:', response.data.length);
      console.log('📋 Orders status:', response.data.map(o => ({ id: o._id, status: o.status })));
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching active orders:', error);
      throw error;
    }
  },

  // Get all orders for a branch (including completed ones)
  getOrdersByBranch: async (branchId) => {
    try {
      const response = await api.get(`orders/branch/${branchId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get order by ID
  getOrderById: async (orderId) => {
    try {
      const response = await api.get(`orders/${orderId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get orders by table ID
  getOrdersByTable: async (tableId) => {
    try {
      const response = await api.get(`orders/table/${tableId}`);
      return response.data.orders || response.data; // Handle both new and old format
    } catch (error) {
      throw error;
    }
  },

  // Get orders and table info by table ID.
  // 8s timeout — this call runs when staff tap a table; a 30s stall here is
  // the "app is hanging" experience on a degraded network.
  getOrdersAndTableInfo: async (tableId) => {
    try {
      const response = await api.get(`orders/table/${tableId}`, { timeout: 8000 });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create new order
  createOrder: async (orderData) => {
    try {
      const response = await api.post('orders', orderData, { timeout: POS_WRITE_TIMEOUT_MS });
      return response.data;
    } catch (error) {
      console.error('Failed to create order:', error);
      throw error;
    }
  },

  // Update order
  updateOrder: async (orderId, orderData) => {
    try {
      console.log('orderService.updateOrder called with orderId:', orderId, 'orderData:', orderData);
      console.log('Making PUT request to:', `orders/${orderId}`);
      const response = await api.put(`orders/${orderId}`, orderData, { timeout: POS_WRITE_TIMEOUT_MS });
      console.log('orderService.updateOrder response:', response.data);
      return response.data;
    } catch (error) {
      // orderId + fields in the SAME log line — forensics repeatedly hit walls
      // because these errors never said WHICH order the failing PUT targeted.
      console.error('orderService.updateOrder error:', {
        orderId,
        fields: Object.keys(orderData || {}),
        status: error?.response?.status,
        serverMsg: error?.response?.data?.message,
        errMsg: error?.message,
      });
      throw error;
    }
  },

  // Mark order as served (sets lastServedAt checkpoint)
  markOrderServed: async (orderId, status = 'served') => {
    
    try {
      // Ensure orderId is a string (handle _id format)
      const orderIdStr = typeof orderId === 'object' ? (orderId.id || orderId._id) : String(orderId);
      
      
      console.log('orderService.markOrderServed called with orderId:', orderIdStr, 'status:', status);
      const response = await api.post(`orders/${orderIdStr}/serve`, { status }, { timeout: POS_WRITE_TIMEOUT_MS });
      console.log('orderService.markOrderServed response:', response.data);

      // Backend returns the order directly via transformOrder
      const order = response.data;
      return order;
    } catch (error) {
      
      console.error('orderService.markOrderServed error:', error);
      console.error('Error details:', error.response?.data);
      throw error;
    }
  },

  // Update order status (convenience method for KDS)
  updateOrderStatus: async (orderId, status, updates = {}) => {
    try {
      console.log('orderService.updateOrderStatus called with orderId:', orderId, 'status:', status);
      const response = await api.put(`orders/${orderId}`, { status, ...updates }, { timeout: POS_WRITE_TIMEOUT_MS });
      console.log('orderService.updateOrderStatus response:', response.data);
      return response.data;
    } catch (error) {
      console.error('orderService.updateOrderStatus error:', error);
      throw error;
    }
  },

  // Merge multiple source orders into one destination order.
  // POS write timeout: on a half-dead network a merge without a timeout hangs
  // the merge modal for the OS TCP timeout (~30s+). Merge is blocked offline
  // at the call site; this covers the "looks online but isn't" case.
  mergeOrders: async (destinationOrderId, sourceOrderIds) => {
    try {
      const response = await api.post('orders/merge', { destinationOrderId, sourceOrderIds }, { timeout: POS_WRITE_TIMEOUT_MS });
      return response.data;
    } catch (error) {
      console.error('orderService.mergeOrders error:', error);
      throw error;
    }
  },

  // Delete order (marks as deleted, doesn't actually delete from database).
  // `reason` = { reasonCode, reason } — travels as a DELETE body so the server
  // can record WHY the order was removed (and is forced to soft-delete, so the
  // audit record survives).
  deleteOrder: async (orderId, reason = null) => {
    try {
      console.log('orderService.deleteOrder called with orderId:', orderId, reason?.reasonCode || '');
      const response = await api.delete(`orders/${orderId}`, reason ? { data: reason } : undefined);
      console.log('orderService.deleteOrder response:', response.data);
      return response.data;
    } catch (error) {
      console.error('orderService.deleteOrder error:', error);
      throw error;
    }
  },

  // Calculate order totals
  calculateTotals: (items, discountType = 'none', discountValue = 0) => {
    const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    let discountAmount = 0;
    if (discountType === 'percent') {
      discountAmount = (subtotal * (discountValue / 100));
    } else if (discountType === 'amount') {
      discountAmount = discountValue;
    }
    
    const taxRate = 0.1; // 10% tax
    const tax = (subtotal - discountAmount) * taxRate;
    const total = subtotal - discountAmount + tax;
    
    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      discountAmount: parseFloat(discountAmount.toFixed(2)),
      tax: parseFloat(tax.toFixed(2)),
      total: parseFloat(total.toFixed(2))
    };
  },

  // Get order history
  getOrderHistory: async (branchId, page = 1, limit = 20, startDate = null, endDate = null, searchTerm = null, statusFilter = null, timezone = null) => {
    const params = new URLSearchParams({
      branchId,
      page: page.toString(),
      limit: limit.toString()
    });

    // Send local date strings as-is. The backend's resolveSessionDateRange
    // already converts local dates to UTC using the branch timezone internally,
    // so pre-converting here causes a double-shift (off-by-one-day for UTC+ zones).
    if (startDate) params.append('startDate', startDate);
    if (endDate)   params.append('endDate', endDate);

    if (searchTerm) {
      params.append('searchTerm', searchTerm);
    }

    if (statusFilter) {
      params.append('statusFilter', statusFilter);
    }

    const response = await api.get(`/orders/history?${params}`);
    return response.data;
  },

  getOrdersStatsByUser: async (branchId, userId, startDate, endDate) => {
    const params = new URLSearchParams({
      branchId,
      userId,
      startDate,
      endDate
    });
    const response = await api.get(`orders/stats/by-user?${params}`);
    return response.data;
  },

  getOrdersStatsMy: async (startDate, endDate, branchId = null) => {
    const params = new URLSearchParams({
      startDate,
      endDate
    });
    if (branchId) {
      params.append('branchId', branchId);
    }
    const response = await api.get(`orders/stats/my?${params}`);
    return response.data;
  }
};

// Get menu item analytics (kept as named export for backward compatibility)
export const getMenuItemAnalytics = async (branchId, startDate = null, endDate = null) => {
  const params = new URLSearchParams({
    branchId
  });
  
  if (startDate) {
    params.append('startDate', startDate);
  }
  
  if (endDate) {
    params.append('endDate', endDate);
  }
  
  const response = await api.get(`/orders/analytics?${params}`);
  return response.data;
};

// Get order modification details
export const getOrderModifications = async (orderId) => {
  try {
    const response = await api.get(`orders/${orderId}/modifications`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get orders with modifications for a specific date range
export const getModifiedOrders = async (branchId, startDate = null, endDate = null) => {
  try {
    const params = new URLSearchParams({
      branchId,
      statusFilter: 'modified'
    });
    
    if (startDate) {
      params.append('startDate', startDate);
    }
    
    if (endDate) {
      params.append('endDate', endDate);
    }
    
    const response = await api.get(`/orders/history?${params}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get orders with modifications grouped by order ID
export const getModifiedOrdersGrouped = async (branchId, startDate = null, endDate = null) => {
  try {
    const params = new URLSearchParams({
      branchId,
      statusFilter: 'modified',
      groupBy: 'orderId'
    });
    
    if (startDate) {
      params.append('startDate', startDate);
    }
    
    if (endDate) {
      params.append('endDate', endDate);
    }
    
    const response = await api.get(`/orders/history?${params}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get modification statistics for a branch
export const getModificationStats = async (branchId, startDate = null, endDate = null) => {
  try {
    const params = new URLSearchParams({
      branchId
    });
    
    if (startDate) {
      params.append('startDate', startDate);
    }
    
    if (endDate) {
      params.append('endDate', endDate);
    }
    
    const response = await api.get(`/orders/modification-stats?${params}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get detailed modification history for an order
export const getOrderModificationHistory = async (orderId) => {
  try {
    const response = await api.get(`orders/${orderId}/modifications`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Test function to simulate order modification (for development/testing)
export const testOrderModification = async (orderId, modifications) => {
  try {
    console.log('Testing order modification for order:', orderId);
    console.log('Modifications to apply:', modifications);
    
    const response = await api.put(`orders/${orderId}/test-modification`, modifications);
    return response.data;
  } catch (error) {
    console.error('Error testing order modification:', error);
    throw error;
  }
};

// Get orders that are eligible for modification (completed orders)
export const getModifiableOrders = async (branchId) => {
  try {
    const response = await api.get(`orders/modifiable?branchId=${branchId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Test modification tracking for an order
export const testModificationTracking = async (orderId) => {
  try {
    console.log('Testing modification tracking for order:', orderId);
    const response = await api.post(`orders/${orderId}/test-modification`);
    console.log('Test modification response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Test modification error:', error);
    throw error;
  }
};

export default orderService; 